import type { ProductResponse } from '@malaberg/shared';
/**
 * ProductsTab — T27.
 *
 * Admin+ view of products with full CRUD.
 * Features:
 *   - Mantine Table listing current products
 *   - Toggle "Show inactive" checkbox
 *   - Per-row: Edit (ProductEditor modal), Revert (version history drawer), Archive (confirm dialog)
 *   - New product button → ProductEditor in create mode
 */
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconArchive, IconEdit, IconHistory, IconPlus } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { productsApi } from '../../api/endpoints/products';
import { ProductEditor } from '../../components/settings/ProductEditor';
import type { SelectedSourcePackFile } from '../../components/settings/SourcePackFilePicker';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProductWithSourcePack extends ProductResponse {
  sourcePackFiles?: SelectedSourcePackFile[];
}

interface ProductVersion {
  id: string;
  version: number;
  name: string;
  active: boolean;
  markets: string[];
  isCurrent: boolean;
  createdAt: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProductsTab() {
  // ── List state ────────────────────────────────────────────────────────────
  const [products, setProducts] = useState<ProductWithSourcePack[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // ── Editor modal ──────────────────────────────────────────────────────────
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductWithSourcePack | undefined>(
    undefined
  );

  // ── Archive dialog ────────────────────────────────────────────────────────
  const [archiveTarget, setArchiveTarget] = useState<ProductResponse | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // ── Version history drawer ────────────────────────────────────────────────
  const [historyProductId, setHistoryProductId] = useState<string | null>(null);
  const [versionHistory, setVersionHistory] = useState<ProductVersion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertLoading, setRevertLoading] = useState<number | null>(null);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  async function fetchProducts() {
    setLoading(true);
    setListError(null);
    try {
      const result = await productsApi.list({ limit: 200 });
      const data = result as { ok: boolean; items: ProductWithSourcePack[]; total: number };
      const allProducts = data.items;

      // Filter by active unless showInactive is toggled
      setProducts(showInactive ? allProducts : allProducts.filter((p) => p.active));
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  // ─── Archive ───────────────────────────────────────────────────────────────

  async function handleArchiveConfirm() {
    if (!archiveTarget) return;
    setArchiveLoading(true);
    try {
      await productsApi.delete(archiveTarget.id);
      setArchiveTarget(null);
      await fetchProducts();
    } catch (err) {
      setArchiveTarget(null);
    } finally {
      setArchiveLoading(false);
    }
  }

  // ─── Version history ───────────────────────────────────────────────────────

  async function openHistory(productId: string) {
    setHistoryProductId(productId);
    setHistoryLoading(true);
    try {
      const result = await productsApi.getVersions(productId);
      const data = result as { ok: boolean; versions?: ProductVersion[]; items?: ProductVersion[] };
      setVersionHistory(data.versions ?? data.items ?? []);
    } catch {
      setVersionHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleRevert(productId: string, version: number) {
    setRevertLoading(version);
    try {
      await productsApi.revert(productId, version);
      setHistoryProductId(null);
      await fetchProducts();
    } catch {
      // Error surfaced in console
    } finally {
      setRevertLoading(null);
    }
  }

  // ─── Editor helpers ────────────────────────────────────────────────────────

  function openCreate() {
    setEditingProduct(undefined);
    setEditorOpen(true);
  }

  function openEdit(product: ProductWithSourcePack) {
    setEditingProduct(product);
    setEditorOpen(true);
  }

  function handleEditorClose() {
    setEditorOpen(false);
    setEditingProduct(undefined);
  }

  async function handleEditorSuccess() {
    await fetchProducts();
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Stack data-testid="products-tab" gap="md">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <Group justify="space-between">
        <Checkbox
          data-testid="show-inactive-toggle"
          label="Show inactive"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.currentTarget.checked)}
        />
        <Button
          data-testid="new-product-btn"
          leftSection={<IconPlus size={16} />}
          onClick={openCreate}
        >
          New product
        </Button>
      </Group>

      {/* ── Error state ───────────────────────────────────────────────────── */}
      {listError && (
        <Alert color="red" data-testid="products-list-error">
          {listError}
        </Alert>
      )}

      {/* ── Products table ────────────────────────────────────────────────── */}
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>ID</Table.Th>
            <Table.Th>Markets</Table.Th>
            <Table.Th>Version</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text ta="center" c="dimmed" size="sm">
                  Loading…
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && products.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text ta="center" c="dimmed" size="sm">
                  No products found
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading &&
            products.map((p) => (
              <Table.Tr key={`${p.id}-${p.version}`} data-testid={`product-row-${p.id}`}>
                <Table.Td>{p.name}</Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {p.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    {p.markets.map((m) => (
                      <Badge key={m} size="sm" variant="light">
                        {m}
                      </Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td>v{p.version}</Table.Td>
                <Table.Td>
                  <Badge color={p.active ? 'green' : 'gray'} size="sm">
                    {p.active ? 'active' : 'inactive'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {/* Edit */}
                    <Tooltip label="Edit product">
                      <ActionIcon
                        data-testid="product-edit-btn"
                        variant="subtle"
                        size="sm"
                        onClick={() => openEdit(p)}
                        aria-label="Edit product"
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>

                    {/* Version history / Revert */}
                    <Tooltip label="Version history">
                      <ActionIcon
                        data-testid="product-revert-btn"
                        variant="subtle"
                        size="sm"
                        color="violet"
                        onClick={() => openHistory(p.id)}
                        aria-label="Version history"
                      >
                        <IconHistory size={16} />
                      </ActionIcon>
                    </Tooltip>

                    {/* Archive */}
                    <Tooltip label="Archive product">
                      <ActionIcon
                        data-testid="product-archive-btn"
                        variant="subtle"
                        size="sm"
                        color="red"
                        onClick={() => setArchiveTarget(p)}
                        aria-label="Archive product"
                      >
                        <IconArchive size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </Table>

      {/* ── Archive confirm dialog ────────────────────────────────────────── */}
      <Modal
        opened={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        title="Archive product"
        centered
        size="sm"
      >
        <Stack data-testid="archive-dialog">
          <Text>
            Archive <strong>{archiveTarget?.name}</strong>? The product will be marked inactive and
            removed from the brief form. This action can be reverted via version history.
          </Text>
          <Group justify="flex-end">
            <Button
              data-testid="archive-cancel-btn"
              variant="default"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="archive-confirm-btn"
              color="red"
              loading={archiveLoading}
              onClick={handleArchiveConfirm}
            >
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Version history drawer ────────────────────────────────────────── */}
      <Drawer
        opened={historyProductId !== null}
        onClose={() => {
          setHistoryProductId(null);
          setVersionHistory([]);
        }}
        title="Version history"
        position="right"
        size="md"
        data-testid="version-history-drawer"
      >
        <Stack>
          {historyLoading && (
            <Text c="dimmed" size="sm">
              Loading history…
            </Text>
          )}
          {!historyLoading && versionHistory.length === 0 && (
            <Text c="dimmed" size="sm">
              No previous versions found.
            </Text>
          )}
          {!historyLoading &&
            versionHistory.map((v) => (
              <Box
                key={v.version}
                data-testid={`version-row-${v.version}`}
                style={{
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 8,
                  padding: '12px',
                }}
              >
                <Group justify="space-between">
                  <Stack gap={2}>
                    <Text fw={500}>
                      v{v.version} — {v.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Markets: {v.markets.join(', ')} • {new Date(v.createdAt).toLocaleDateString()}
                    </Text>
                  </Stack>
                  <Button
                    data-testid={`revert-to-v${v.version}`}
                    size="xs"
                    variant="light"
                    loading={revertLoading === v.version}
                    onClick={() => historyProductId && handleRevert(historyProductId, v.version)}
                  >
                    Revert to this version
                  </Button>
                </Group>
              </Box>
            ))}
        </Stack>
      </Drawer>

      {/* ── ProductEditor modal ───────────────────────────────────────────── */}
      <ProductEditor
        opened={editorOpen}
        onClose={handleEditorClose}
        onSuccess={handleEditorSuccess}
        product={editingProduct}
      />
    </Stack>
  );
}
